import AssetsBase from "./data/assets-base.js";
import ElementsByType from "./data/elements-by-type.js";
import AssetsByType from "./data/assets-by-type.js";
import CurrentSlide from "./data/current-slide.js";
import SlideAssets from "./data/slide-assets.js";
import SlideCamera from "./data/slide-camera.js";
import SlideEnvLight from "./data/slide-envlight.js";
import DeepMerge from "./data/deep-merge.js";
import ObjectChildren from "./data/object-children.js";
import ObjectNames from "./data/object-names.js";
import AnimationRoots from "./data/animation-roots.js";
import ObjectRoots from "./data/object-roots.js";
import ObjectAnimations from "./data/object-animations.js";
import LiveLabels from "./data/live-labels.js";
import ObjectOnScreenPositions from "./data/object-onscreen-positions.js";
import LiveLabelPositions from "./data/live-label-positions.js";

import MaterialTypeGen from "./data/trackers/assets/materials/type-gen.js";
import { MaterialFullTypes, Unrecognized as UnrecognizedMaterial } from "./data-defs/material-full-types.js";

const MatTypes = [UnrecognizedMaterial].concat(MaterialFullTypes);

const EmptyObj = {};

class Data {
    constructor(wrapCtx, evtCtx) {
        this._wrapCtx = wrapCtx;
        this._evtCtx = evtCtx;

        this._assetsBase = new AssetsBase(wrapCtx, evtCtx);
        this._assetsByType = new AssetsByType(wrapCtx, evtCtx);
        this._currentSlide = new CurrentSlide(wrapCtx, evtCtx);
        this._slideAssets = new SlideAssets(wrapCtx, evtCtx);
        this._slideCamera = new SlideCamera(wrapCtx, evtCtx);
        this._slideEnvLight = new SlideEnvLight(wrapCtx, evtCtx);
        this._slideFullMaterials = new DeepMerge(wrapCtx, evtCtx);
        this._slideFullMaterialsByType = new ElementsByType(wrapCtx, evtCtx, MatTypes, () => new MaterialTypeGen(evtCtx));
        this._slideFullObjects = new DeepMerge(wrapCtx, evtCtx);
        this._objectChildren = new ObjectChildren(wrapCtx, evtCtx);
        this._objectNames = new ObjectNames(wrapCtx, evtCtx);
        this._animationRoots = new AnimationRoots(wrapCtx, evtCtx);
        this._objectRoots = new ObjectRoots(wrapCtx, evtCtx);
        this._objectAnimations = new ObjectAnimations(wrapCtx, evtCtx);
        this._liveLabels = new LiveLabels(wrapCtx, evtCtx);
        this._objectOnScreenPositions = new ObjectOnScreenPositions(wrapCtx, evtCtx);
        this._liveLabelPositions = new LiveLabelPositions(wrapCtx, evtCtx);
    }

    get assetsBase() { return this._assetsBase; }
    get assetsByType() { return this._assetsByType; }
    get currentSlide() { return this._currentSlide; }
    get slideAssets() { return this._slideAssets; }
    get slideCamera() { return this._slideCamera; }
    get slideEnvLight() { return this._slideEnvLight; }
    get slideFullMaterials() { return this._slideFullMaterials; }
    get slideFullMaterialsByType() { return this._slideFullMaterialsByType; }
    get slideFullObjects() { return this._slideFullObjects; }
    get objectChildren() { return this._objectChildren; }
    get objectNames() { return this._objectNames; }
    get animationRoots() { return this._animationRoots; }
    get objectRoots() { return this._objectRoots; }
    get objectAnimations() { return this._objectAnimations; }
    get liveLabels() { return this._liveLabels; }
    get objectOnScreenPositions() { return this._objectOnScreenPositions; }
    get liveLabelPositions() { return this._liveLabelPositions; }

    track(tour, tourLabels, session, objOnScreenPos) {
        if (tour || tourLabels || session) {
            const { data: tourData, events: tourEvents } = tour || EmptyObj;
            const { data: tourLabelsData, events: tourLabelsEvents } = tourLabels || EmptyObj;
            const { data: sessionData, events: sessionEvents } = session || EmptyObj;

            this._assetsBase.track(tourData, tourEvents);
            this._assetsByType.track(this._assetsBase.assets);
            this._currentSlide.track(tourData, tourEvents, sessionData, sessionEvents);
            this._slideAssets.track(this._currentSlide.slide);
            this._slideCamera.track(this._currentSlide.slide);
            this._slideEnvLight.track(this._currentSlide.slide);
            this._slideFullMaterials.track(1, this._assetsBase.assets.materials);
            this._slideFullMaterials.track(0, this._slideAssets.assets.materials);
            this._slideFullMaterialsByType.track(this._slideFullMaterials.merged);
            this._slideFullObjects.track(1, this._assetsBase.assets.objects);
            this._slideFullObjects.track(0, this._slideAssets.assets.objects);
            this._objectChildren.track(this._assetsBase.assets.objects);
            this._objectNames.track(this._assetsBase.assets.objects);
            this._animationRoots.track(this._slideAssets.assets.objects);
            this._objectRoots.track(this._objectChildren.children, this._animationRoots.roots);
            this._objectAnimations.track(this._slideAssets.assets.objects, this._objectRoots.roots);
            this._liveLabels.track(tourLabelsData, tourLabelsEvents);
            this._objectOnScreenPositions.track(objOnScreenPos);
            this._liveLabelPositions.track(this._liveLabels.labels, this._objectOnScreenPositions.positions);
        } else {
            this._assetsBase.track();
            this._assetsByType.track();
            this._currentSlide.track();
            this._slideAssets.track();
            this._slideCamera.track();
            this._slideEnvLight.track();
            this._slideFullMaterials.track(1, null);
            this._slideFullMaterials.track(0, null);
            this._slideFullMaterialsByType.track();
            this._slideFullObjects.track(1, null);
            this._slideFullObjects.track(0, null);
            this._objectChildren.track();
            this._objectNames.track();
            this._animationRoots.track();
            this._objectRoots.track();
            this._objectAnimations.track();
            this._liveLabels.track();
            this._objectOnScreenPositions.track();
            this._liveLabelPositions.track();
        }

        this.update();
    }

    update() {
        this._assetsBase.update();
        this._assetsByType.update();
        this._currentSlide.update();
        this._slideAssets.update();
        this._slideCamera.update();
        this._slideEnvLight.update();
        this._slideFullMaterials.update();
        this._slideFullMaterialsByType.update();
        this._slideFullObjects.update();
        this._objectChildren.update();
        this._objectNames.update();
        this._animationRoots.update();
        this._objectRoots.update();
        this._objectAnimations.update();
        this._liveLabels.update();
        this._objectOnScreenPositions.update();
        this._liveLabelPositions.update();
    }
}

export default Data;
